import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  GridApi,
  IHeaderGroupParams,
} from '@ag-grid-community/core';
import { ISODate } from '@frk/eds-components';
import {
  NAV_COLS_ALWAYS_SHOW,
  PERFORMANCE_COLS_ALWAYS_SHOW,
  ProductColGroupId,
  ProductColId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import {
  CalcTypeConfig,
  getCalcTypeConfig,
} from '@products/fund-listing/services/calc-type.config';
import { SiteConfigService } from '@services';
import {
  CalcTypeStd,
  ConfigurationId,
  CurrencyCode,
  FundShareClassId,
  PerformanceType,
  PerformanceView,
  PerformanceViewOption,
  ProductType,
  ToggleProps,
  WebProductTaxonomy,
} from '@types';
import { Logger } from '@utils/logger';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FundListingCalcTypeController,
  FundListingController,
  FundListingType,
} from './fund-listing.controller';

const logger = Logger.getLogger('FundListingPerformanceController');

export class FundListingPerformanceController
  implements FundListingController, FundListingCalcTypeController {
  public fundListingType: FundListingType = FundListingType.PERFORMANCE;
  public productTaxonomy?: WebProductTaxonomy;

  // config option
  public hideFavoriteCol?: boolean;
  public showRemoveFavoriteCol?: boolean;
  public hideFactSheetCol?: boolean;

  // set this externally, if you want to be able to update favorites
  public updateFavourite?: (fund: FundShareClassId) => void;

  // used to toggle "hide" prop on performance cols etc
  // not ideal solution, as this risks circular dependencies. But necessary for card views
  private colDefs: (ColGroupDef | ColDef)[];

  // APIs will only be available in Ag Grid view - not card view
  private gridApi: GridApi;
  private colApi: ColumnApi;

  // emits when colDefs are changed (eg hide toggled on cols)
  // used to know when to update card views
  private refresh$: Subject<void> = new Subject<void>();

  // calcType + config
  private calcTypeConfig: CalcTypeConfig;
  private readonly calcTypeStd$: BehaviorSubject<CalcTypeStd>;

  // PerformanceViewOptions. TODO: factor out
  public readonly performanceViewOptions: PerformanceViewOption[];
  private readonly performanceViewOption$: BehaviorSubject<PerformanceViewOption>;

  // PerformanceTypeOptions
  private readonly performanceType$: BehaviorSubject<PerformanceType>;

  private periodEnd$: BehaviorSubject<PerformanceView>;
  private defaultPeriodEnd: PerformanceView;

  // determines if calc type group header shold show in card view.
  // not applicable for grid, as calc type header is in col group config
  public showCalcTypeHeader = false;

  private performanceCurrency$: BehaviorSubject<CurrencyCode>;

  constructor(
    protected siteConfig: SiteConfigService,
    public readonly productType: ConfigurationId,
    public componentName: string,
    // TODO: can as-of dates be optional?
    public readonly performanceAsOfDateMonthlyStd: ISODate,
    public readonly performanceAsOfDateQuarterlyStd: ISODate,
    public readonly performanceAsOfDateMonthly: string,
    public readonly performanceAsOfDateQuarterly: string,
    public readonly navAsOfDate: string, // null for SMAs
    public readonly performanceCurrencies?: CurrencyCode[], // only needed for South Africa. Come from data - not config
    public readonly hasDataMigrated?: boolean
  ) {
    logger.debug(
      'constructor()',
      this.productType,
      this.componentName,
      this.performanceAsOfDateMonthlyStd,
      this.performanceAsOfDateQuarterlyStd,
      this.performanceAsOfDateMonthly,
      this.performanceAsOfDateQuarterly,
      this.navAsOfDate,
      this.performanceCurrencies,
      this.hasDataMigrated
    );
    // set initial calcTypeConfig
    this.calcTypeConfig = getCalcTypeConfig(
      this.productType,
      this.siteConfig.getPdsCountry()
    );
    // set initial calc type
    this.calcTypeStd$ = new BehaviorSubject<CalcTypeStd>(
      this.getDefaultCalcType()
    );

    // set initial performance view options
    this.performanceViewOptions = this.siteConfig.getPerformanceViewOptions(
      productType as ProductType
    );

    // set default performance view
    const defaultPerformanceView: PerformanceViewOption =
      this.performanceViewOptions.find(
        (view: PerformanceViewOption): boolean => view.isDefault
      ) || null;
    this.performanceViewOption$ = new BehaviorSubject<PerformanceViewOption>(
      defaultPerformanceView
    );

    // set default performance type
    const defaultPerformanceType: PerformanceType =
      defaultPerformanceView?.name || null;
    this.performanceType$ = new BehaviorSubject<PerformanceType>(
      defaultPerformanceType
    );

    // set default period end
    this.defaultPeriodEnd = this.performanceViewOption$.getValue()?.periodEnds[0];
    this.periodEnd$ = new BehaviorSubject<PerformanceView>(
      this.defaultPeriodEnd
    );

    // set default currency options (South Africa only)
    let selectedCurrencyOption: CurrencyCode = null;
    if (this.siteConfig.showPerformanceCurrency()) {
      // Intentionally added at first place to have default calculation as BAU.
      this.performanceCurrencies?.splice(0, 0, 'SHARE_CLASS' as CurrencyCode);
      // default to first option
      selectedCurrencyOption = this.performanceCurrencies?.[0] || null;
    }
    this.performanceCurrency$ = new BehaviorSubject<CurrencyCode>(
      selectedCurrencyOption
    );

    // TODO: unsubscribe. Need to add Destroy hook into Controller
    // subscribe to performanceType$ and periodEnd$ to update column defs
    combineLatest([this.performanceType$, this.periodEnd$]).subscribe(
      ([performanceType, performanceView]: [
        PerformanceType,
        PerformanceView
      ]): void => {
        logger.debug('combineLatest', performanceType, performanceView);
        this.togglePerformanceColumns(performanceType, performanceView);
      }
    );
  }

  // setters
  public setColDefs = (colDefs: ColGroupDef[] | ColDef[]): void => {
    logger.debug('setColDefs()', colDefs);
    this.colDefs = colDefs;

    // set initial columns visibility
    this.toggleNAVCols(this.getSelectedCalcType());
    this.togglePerformanceColumns(
      this.performanceType$.getValue(),
      this.periodEnd$.getValue()
    );

    // determine if calc type header should be visible in card view
    this.showCalcTypeHeader = colDefs.some(
      (group: ColGroupDef): boolean =>
        group.headerGroupComponent === 'calcTypeGroupHeaderComponent'
    );
  };

  public setGridAPIs = (params: IHeaderGroupParams): void => {
    logger.debug('setGridAPIs()', params);
    if (!this.gridApi) {
      this.gridApi = params.api;
      this.colApi = params.columnApi;

      // set initial columns visibility
      this.toggleNAVCols(this.getSelectedCalcType());
      this.togglePerformanceColumns(
        this.performanceType$.getValue(),
        this.periodEnd$.getValue()
      );
    }
  };

  public setCalcType = (selected: CalcTypeStd): void => {
    logger.debug('setCalcType()', selected);
    this.calcTypeStd$.next(selected);
    this.toggleNAVCols(selected);
  };

  public setPerformanceType = (performanceType: PerformanceType): void => {
    logger.debug('setPerformanceType()', performanceType);
    this.performanceType$.next(performanceType);
    // reset period end to default when performance type changes
    this.periodEnd$.next(this.defaultPeriodEnd);
  };

  public setPeriodEnd = (periodEnd: PerformanceView): void => {
    logger.debug('setPeriodEnd()', periodEnd);
    this.periodEnd$.next(periodEnd);
  };

  public setPerformanceCurrency = (currencyCode: CurrencyCode): void => {
    logger.debug('setPerformanceCurrency()', currencyCode);
    this.performanceCurrency$.next(currencyCode);
    this.refreshPerformanceCols();
  };

  // getters
  public getRefresh$ = (): Observable<void> => this.refresh$;
  public getCalcType$ = (): Observable<CalcTypeStd> => this.calcTypeStd$;
  public getSelectedCalcType = (): CalcTypeStd => this.calcTypeStd$.getValue();
  public getSelectedCalcTypes = (): CalcTypeStd[] => {
    const selectedCalcType: CalcTypeStd = this.getSelectedCalcType();
    if (typeof this.calcTypeConfig === 'string') {
      return [selectedCalcType];
    } else {
      const calcTypeConfig = this.calcTypeConfig as ToggleProps;
      return calcTypeConfig.applicableValues &&
        selectedCalcType in calcTypeConfig.applicableValues
        ? calcTypeConfig.applicableValues[selectedCalcType]
        : [selectedCalcType];
    }
  };
  // gets config with currently selected value
  public getCalcTypeConfig = (): CalcTypeConfig => {
    const selectedCalcType: CalcTypeStd = this.getSelectedCalcType();
    if (typeof this.calcTypeConfig === 'string') {
      return selectedCalcType;
    } else {
      const calcTypeConfig = this.calcTypeConfig as ToggleProps;
      return { ...calcTypeConfig, selected: selectedCalcType };
    }
  };

  public getPerformanceAsOf$ = (): Observable<string> =>
    this.periodEnd$.pipe(map(this.calcPerformanceAsOf));
  public getPerformanceAsOf = (): string =>
    this.calcPerformanceAsOf(this.periodEnd$.getValue());
  public getPerformanceViewOption$ = (): BehaviorSubject<PerformanceViewOption> =>
    this.performanceViewOption$;
  public getPeriodEnd$ = (): BehaviorSubject<PerformanceView> =>
    this.periodEnd$;
  public getPerformanceCurrency = (): string =>
    this.performanceCurrency$.getValue();

  // private functions
  private toggleNAVCols = (selected: CalcTypeStd): void => {
    if (this.siteConfig.canToggleNAVColumns(this.productType)) {
      // US+CA can toggle columns shown for CEF+ETF
      logger.debug('canToggleNAVColumns()');
      const navCols: ColDef[] = this.getGroupCols(ProductColGroupId.NAV);
      logger.debug('navCols', selected, navCols);

      if (selected === CalcTypeStd.NAV) {
        this.showColumns(
          navCols,
          (col: ColDef): boolean =>
            col.colId.startsWith(ProductColGroupId.NAV) ||
            NAV_COLS_ALWAYS_SHOW.includes(col.colId as ProductColId)
        );
      } else if (selected === CalcTypeStd.MARKET_PRICE) {
        this.showColumns(
          navCols,
          (col: ColDef): boolean =>
            col.colId.startsWith('MARKETPRICE') ||
            NAV_COLS_ALWAYS_SHOW.includes(col.colId as ProductColId)
        );
      }
    }
    // refresh card values
    this.refresh$.next();

    if (this.gridApi) {
      // refresh grid values
      this.gridApi.refreshCells({
        columns: this.colApi
          .getColumnGroup(ProductColGroupId.PERFORMANCE)
          ?.getLeafColumns(),
      });

      if (
        this.colApi.getColumnGroup(ProductColGroupId.ADDITIONAL_PERFORMANCE)
      ) {
        this.gridApi.refreshCells({
          columns: this.colApi
            .getColumnGroup(ProductColGroupId.ADDITIONAL_PERFORMANCE)
            ?.getLeafColumns(),
        });
      }
    }
  };

  private togglePerformanceColumns = (
    performanceType: PerformanceType,
    periodEnd: PerformanceView
  ): void => {
    logger.debug(
      'togglePerformanceColumns()',
      !!this.colApi,
      performanceType,
      periodEnd
    );

    // LOGIC: NGC-1351.
    // For intl site, the inception column without M/Q suffixes should get displayed.
    // This is for perf-type as 'CALENDAR_YEAR' and 'DISCRETE'.
    const inceptionCol = `${performanceType}-INCEPTION`;

    // show/hide performance cols based on period end and type
    const currentPerf = `${performanceType}-${periodEnd}`;
    const performanceCols: ColDef[] = this.getGroupCols(
      ProductColGroupId.PERFORMANCE
    );

    // handle performanceViewOptions.length === 0. should hide all performance cols
    // and show inception date col instead
    let defaultColId: ProductColId;
    if (!performanceType) {
      // NGC-6385 default to show Calendar Year Inception col when no performanceTypes configured
      defaultColId = ProductColId.CALENDAR_YEAR_INCEPTION;
    }

    logger.debug('performanceCols', currentPerf, performanceCols);
    this.showColumns(
      performanceCols,
      (col: ColDef): boolean =>
        PERFORMANCE_COLS_ALWAYS_SHOW.includes(col.colId as ProductColId) ||
        inceptionCol === col.colId ||
        col.colId.startsWith(currentPerf) ||
        col.colId === defaultColId
    );

    // if ADDITIONAL_PERFORMANCE group, show appropriate monthly/Quarterly columns
    const additionalPerformanceCols: ColDef[] = this.getGroupCols(
      ProductColGroupId.ADDITIONAL_PERFORMANCE
    );

    if (additionalPerformanceCols?.length > 0) {
      this.showColumns(
        additionalPerformanceCols,
        (col: ColDef): boolean => col.colId.indexOf(`-${periodEnd}-`) > -1
      );
    }

    // emit new performance view option
    const newOption: PerformanceViewOption = this.performanceViewOptions.find(
      (opt: PerformanceViewOption): boolean => opt.name === performanceType
    );
    if (newOption) {
      this.performanceViewOption$.next(newOption);
    }

    this.refreshPerformanceCols();
  };

  private showColumns(
    columns: ColDef[],
    predicate: (col: ColDef) => boolean
  ): void {
    columns.forEach((col: ColDef): void => {
      col.hide = !predicate(col);
    });

    if (this.colApi) {
      const showCols = columns
        .filter((col: ColDef): boolean => predicate(col))
        .map((col) => col.colId);

      const hideCols = columns
        .filter((col: ColDef): boolean => !predicate(col))
        .map((col) => col.colId);

      logger.debug('showColumns', hideCols, showCols);

      // TODO:
      // hiding cols first stops footnote placement being lost
      // BUT showing cols first stops second PerformanceGroupHeader being created
      // refreshing header causes infinte loop
      // NEED a way to stop ALL cols in group being hidden at once??
      // this is an issue for NJBest
      /**
       * orders of these calls should be like this,
       * as hidecols after show call was removing the subscription of caveats ,
       * due to which they were getting removed on clicking toggles
       */
      this.colApi.setColumnsVisible(hideCols, false);
      this.colApi.setColumnsVisible(showCols, true);
    }
  }

  private refreshPerformanceCols = (): void => {
    if (this.colApi) {
      this.gridApi.refreshCells({
        columns: this.colApi
          .getColumnGroup(ProductColGroupId.PERFORMANCE)
          ?.getLeafColumns(),
      });
    }

    this.refresh$.next();
  };

  private getDefaultCalcType(): CalcTypeStd {
    // get default from site config if it exists
    const siteConfigDefault: CalcTypeStd = this.siteConfig.getDefaultCalcType(
      this.productType as ProductType
    );
    if (siteConfigDefault) {
      return siteConfigDefault;
    }

    // otherwise get from hard coded config
    if (typeof this.calcTypeConfig === 'string') {
      return this.calcTypeConfig;
    } else if (this.calcTypeConfig) {
      return this.calcTypeConfig.selected; // default to config value
    }
  }

  private calcPerformanceAsOf = (periodEnd: PerformanceView): string =>
    periodEnd === PerformanceView.MONTHLY
      ? this.performanceAsOfDateMonthly
      : this.performanceAsOfDateQuarterly;

  private getGroupCols = (groupId: ProductColGroupId): ColDef[] => {
    const group: ColGroupDef = this.colDefs?.find(
      (grp: ColGroupDef): boolean => grp.groupId === groupId
    ) as ColGroupDef;
    return group?.children || [];
  };
}

export const isFundListingPerformanceController = (
  object: any
): object is FundListingPerformanceController =>
  object.fundListingType === FundListingType.PERFORMANCE;
